// MetricsPage 의 광고 퍼널·가입 퍼널 보조 패널. (MetricsPage 에서 동작 그대로 옮김)
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import type { AdFunnelSummaryResponse, FunnelImportantIssue, buildAdFunnelRows } from '../../types/metrics';
import { formatMetricNumber, formatMetricPercent } from './metricFormat';

export function formatAdFunnelStepLabel(code: string) {
  switch (code) {
    case 'ad_slot_eligible':
      return '슬롯 적격';
    case 'ad_slot_rendered':
      return '슬롯 렌더';
    case 'ad_impression':
      return '노출';
    case 'ad_reward_granted':
      return '리워드 지급';
    case 'ad_slot_failed':
      return '슬롯 실패';
    case 'ad_dismissed':
      return '광고 닫기';
    default:
      return code;
  }
}

export function AdFunnelPlatformPanel({
  title,
  rows,
}: {
  title: string;
  rows: ReturnType<typeof buildAdFunnelRows>;
}) {
  return (
    <div className="rounded-lg border p-4">
      <p className="text-sm font-semibold">{title}</p>
      <div className="mt-3 space-y-2">
        {rows.map((row) => (
          <div key={row.code} className="flex items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">{formatAdFunnelStepLabel(row.code)}</span>
            <span className="font-medium">
              {formatMetricNumber(row.count)} / {formatMetricPercent(row.conversionFromPrevious)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdFunnelBreakdownTable({
  providers,
  placements,
}: {
  providers: AdFunnelSummaryResponse['providers'];
  placements: AdFunnelSummaryResponse['placements'];
}) {
  if ((providers?.length ?? 0) === 0 && (placements?.length ?? 0) === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className="overflow-hidden rounded-lg border">
        <Table mobileCards>
          <TableHeader>
            <TableRow>
              <TableHead>광고 제공자</TableHead>
              <TableHead className="text-right">이벤트</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(providers ?? []).map((provider) => (
              <TableRow key={provider.key}>
                <TableCell>{provider.label}</TableCell>
                <TableCell className="text-right">{formatMetricNumber(provider.eventCount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="overflow-hidden rounded-lg border">
        <Table mobileCards>
          <TableHeader>
            <TableRow>
              <TableHead>배치</TableHead>
              <TableHead className="text-right">이벤트</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(placements ?? []).map((placement) => (
              <TableRow key={placement.key}>
                <TableCell>{placement.label}</TableCell>
                <TableCell className="text-right">{formatMetricNumber(placement.eventCount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function FunnelIssueRow({ issue }: { issue: FunnelImportantIssue }) {
  return (
    <div className="flex flex-col gap-1 rounded-md bg-white/70 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
      <span>{issue.message}</span>
      <Badge variant={issue.severity === 'critical' ? 'destructive' : 'warning'} className="w-fit">
        {issue.severity === 'critical' ? '긴급' : '주의'}
      </Badge>
    </div>
  );
}
